/**
 * Checks made the moment the study page knows the configuration, before a run
 * is sent: the user learns at once what will stop the study, and where to fix
 * it, instead of waiting for the engine to fail half a minute later.
 *
 * Two kinds, kept apart on purpose:
 *
 *  - BLOCKERS are only conditions the backend rejects for certain. Each one
 *    copies the backend's own test, so the page never stops a study that
 *    would have run:
 *      battery   api/services/run_queue.py validate_selected_battery
 *                (applied by api/routers/feasibility.py to every asset that is
 *                not "Standalone PV"): a type with storage needs at least one
 *                selected battery with a name.
 *      design    BESS_feasibility/prepare.py _prepare: a PV asset with an empty
 *                design_dict and auto-design disabled (feasibility runs pass
 *                allow_autodesign=False). design_dict is empty exactly when
 *                systemDesign has no inverter keys (read_config.py).
 *      pv price  simplified costing with no PV price (€/kWp): the PV is priced
 *                at zero and the run fails ("float division by zero"). A PV
 *                that costs nothing is never a valid study either.
 *
 *  - WARNINGS are settings that are probably a leftover default, but may be
 *    intended. They never block; the user can run anyway.
 */
import { assetTypeDefinition } from "../../utils/projectAssets";
import { isBtmSettlement } from "../../components/config/btm";
import { normalizeProjectDebt } from "../../components/config/projectDebt";

const PV_DESIGN_PATH = "/project/asset?tab=pv&pv=design&design=electrical";

/** The battery names the backend reads from bess.selected (run_queue._selected_battery_targets). */
export function selectedBatteryNames(config) {
  const selected = config?.bess?.selected;
  if (!Array.isArray(selected)) return [];
  return selected
    .filter((item) => item && typeof item === "object" && !Array.isArray(item))
    .map((item) => {
      const raw = String(item._key || item.key || item.name || "").trim();
      return raw.includes(":") ? raw.split(":").slice(1).join(":").trim() : raw;
    })
    .filter(Boolean);
}

function hasSystemDesign(config) {
  const design = config?.systemDesign;
  return Boolean(design && typeof design === "object" && Object.keys(design).length > 0);
}

/**
 * @param {object} config  the configuration the run sends (finalPayloadConfig)
 * @returns {{id: string, text: string, fixLabel: string, fixPath: string, secondary?: {label: string, path: string}}[]}
 */
export function preRunBlockers(config, isGreek = false) {
  const tr = (el, en) => (isGreek ? el : en);
  const type = assetTypeDefinition(config?.systemType);
  const out = [];
  if (type?.hasBess && type.value !== "Standalone PV" && selectedBatteryNames(config).length === 0) {
    out.push({
      id: "battery",
      text: type.hasPv
        ? tr("Ο τύπος της μονάδας περιλαμβάνει μπαταρία, αλλά δεν έχει επιλεγεί μπαταρία. Επιλέξτε μπαταρία ή, για μελέτη μόνο Φ/Β, αλλάξτε τον τύπο της μονάδας σε «Αυτόνομος Φ/Β σταθμός».",
          "The unit type includes a battery, but none is selected. Select a battery or, for a PV-only study, change the unit type to \"Standalone PV plant\".")
        : tr("Ο τύπος της μονάδας περιλαμβάνει μπαταρία, αλλά δεν έχει επιλεγεί μπαταρία.",
          "The unit type includes a battery, but none is selected."),
      fixLabel: tr("Επιλογή μπαταρίας", "Select a battery"),
      fixPath: "/project/asset?tab=bess",
      ...(type.hasPv ? { secondary: { label: tr("Αλλαγή τύπου μονάδας", "Change unit type"), path: "/project/asset?tab=general" } } : {}),
    });
  }
  const investment = config?.feasibility?.economics?.investmentCosts || {};
  const simplified = String(investment.costingMode || "").trim().toLowerCase() === "simplified";
  const pvPrice = Number(String(investment.simplePvEurPerKwp ?? "").replace(",", "."));
  if (type?.hasPv && simplified && !(Number.isFinite(pvPrice) && pvPrice > 0)) {
    out.push({
      id: "pv_price",
      text: tr("Η απλοποιημένη κοστολόγηση δεν έχει κόστος Φ/Β (€/kWp), οπότε η επένδυση βγαίνει μηδέν και η μελέτη δεν μπορεί να υπολογιστεί. Συμπληρώστε το «Φ/Β, πλήρως εγκατεστημένο (€/kWp)», χωρίς ΦΠΑ.",
        "Simplified costing has no PV price (€/kWp), so the investment is zero and the study cannot be calculated. Enter \"PV, turnkey (€/kWp)\", before VAT."),
      fixLabel: tr("Κόστος Φ/Β", "PV price"),
      fixPath: "/feasibility/economics#field-econ-simple-pv",
    });
  }
  if (type?.hasPv && !hasSystemDesign(config)) {
    out.push({
      id: "design",
      text: tr("Δεν υπάρχει αποθηκευμένος ηλεκτρολογικός σχεδιασμός Φ/Β. Πατήστε «Αυτόματος σχεδιασμός» και μετά «Αποθήκευση σχεδίου».",
        "There is no saved PV electrical design. Press \"Auto-design\" and then \"Save design\"."),
      fixLabel: tr("Ηλεκτρολογικός σχεδιασμός", "Electrical design"),
      fixPath: PV_DESIGN_PATH,
    });
  }
  return out;
}

/**
 * Probably leftover defaults in a behind-the-meter study. Never blocking.
 * @returns {{id: string, text: string, fixLabel: string, fixPath: string}[]}
 */
export function preRunWarnings(config, isGreek = false) {
  const tr = (el, en) => (isGreek ? el : en);
  if (!isBtmSettlement(config?.settlementType)) return [];
  const out = [];
  const type = assetTypeDefinition(config?.systemType);
  const pvKw = Number(config?.pvInstalledCap?.kw);
  const level = String(config?.grid?.connectionLevel || "").toUpperCase();
  if (type?.hasPv && Number.isFinite(pvKw) && pvKw > 0 && pvKw < 100 && (level === "MV" || level === "HV")) {
    out.push({
      id: "connection_level",
      text: tr(`Η σύνδεση είναι ορισμένη σε ${level === "MV" ? "Μέση" : "Υψηλή"} Τάση, ενώ το Φ/Β είναι ${pvKw.toLocaleString("en-US", { maximumFractionDigits: 2 })} kWp. Μια τέτοια εγκατάσταση συνήθως συνδέεται στη Χαμηλή Τάση (400 V). Η τάση εμφανίζεται στις αναφορές.`,
        `The connection is set to ${level === "MV" ? "medium" : "high"} voltage, while the PV is ${pvKw.toLocaleString("en-US", { maximumFractionDigits: 2 })} kWp. Such a system is usually connected at low voltage (400 V). The voltage appears in the reports.`),
      fixLabel: tr("Σύνδεση στο Δίκτυο", "Grid connection"),
      fixPath: "/project/asset?tab=grid",
    });
  }
  const debt = normalizeProjectDebt(config);
  const percent = Number(debt?.debtPercent);
  const method = debt?.debtSizingMethod || "fixed_debt_percent";
  const fixedPercent = method === "fixed_debt_percent";
  const hasPercent = Number.isFinite(percent) && percent > 0;
  // DSCR sizing borrows whatever the cash flow carries; the "lower of" method
  // borrows nothing when its fixed share is 0.
  if (fixedPercent || method === "lower_of_fixed_percent_and_dscr" ? hasPercent : true) {
    out.push({
      id: "debt",
      text: fixedPercent && hasPercent
        ? tr(`Η μελέτη υποθέτει δάνειο ${percent.toLocaleString("en-US", { maximumFractionDigits: 1 })}% της επένδυσης. Αν ο πελάτης πληρώνει με ίδια κεφάλαια, ορίστε ποσοστό δανεισμού 0.`,
          `The study assumes a loan of ${percent.toLocaleString("en-US", { maximumFractionDigits: 1 })}% of the investment. If the customer pays from own funds, set the debt share to 0.`)
        : tr("Η μελέτη υποθέτει δάνειο. Αν ο πελάτης πληρώνει με ίδια κεφάλαια, ορίστε ποσοστό δανεισμού 0.",
          "The study assumes a loan. If the customer pays from own funds, set the debt share to 0."),
      fixLabel: tr("Οικονομικές Παραδοχές", "Economic assumptions"),
      fixPath: "/feasibility/economics#field-econ-debt-percent",
    });
  }
  return out;
}

/** A key for "the user already chose to run with these warnings". */
export function warningsKey(warnings) {
  return (warnings || []).map((w) => `${w.id}:${w.text}`).join("|");
}
